import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { CircularProgress, TextField } from '@material-ui/core'
import { ErrorOutlineRounded, ExpandMoreRounded, LockRounded } from '@material-ui/icons'
import clsx from 'clsx'

import { getStripe, useStripeLoadFailed } from '../../../../lib/stripe'
import { apiErrorDetails, stripeErrorDetails } from '../../../../lib/stripe-load'
import { bookPayChargeLine, securePaymentLine } from '../../../../lib/payment-copy'
import { customerMessage } from '../../../../lib/api-json'
import {
  BANK_DIDNT_CONFIRM,
  cardErrorMessage,
  cardPayerEmail,
  EMAIL_INVALID,
  isEmailError,
  sameGuest,
  saveAttempt
} from '../../../../lib/payer-email'
import { threeDsStatus, WAITING_FOR_BANK, watchConfirm } from '../../../../lib/confirm-watch'
import {
  applePayRequest,
  cardFormWallet,
  cardSectionOpen,
  cardTriggerTap,
  elementsOptions,
  expressOptions,
  paymentElementOptions,
  platformOf,
  walletAddressFromSheet,
  showCardTrigger,
  walletMethodOf
} from '../../../../lib/checkout-mode'
// OUR OWN POPPINS, FOR STRIPE'S IFRAME. Imported here and not in
// lib/checkout-mode.js because that module is unit tested under plain node,
// which cannot import a .woff2. Each import resolves to the digested URL
// (esbuild emits a RAILS_ASSET_URL marker, Propshaft rewrites it), so these
// are safe to serve with the year-long cache and a replaced face would still
// change its URL.
import poppinsRegular from '../../../../assets/fonts/poppins-v15-latin-regular.woff2'
import poppins500 from '../../../../assets/fonts/poppins-v15-latin-500.woff2'
import poppins600 from '../../../../assets/fonts/poppins-v15-latin-600.woff2'
import { walletNudgeText, walletSheetWatch } from '../../../../lib/wallet-sheet'
import {
  amountForRate,
  NO_POSTCODE,
  walletAddressFromShipping,
  walletAddressOutcome,
  walletMobile,
  zoneFromRate
} from '../../../../lib/wallet-address'
import { isValidUkMobile } from '../../../../lib/mobile'
import { analyticsMissing, platformWallet, unpaidStatus } from '../../../../lib/unpaid-status'
import { AnalyticsService } from '../../../../services/analytics'
import { browserHistory } from '../../../../services/history'
import { promoRefusedAtPay, promoRefusal } from '../../../../lib/promo-credit'
import { confirmationTokenFor, confirmSetupArgs } from '../../../../lib/card-token'
import { apiPromoCodeInfoSuccess, setCreditPreview, setPromoCode, setPromoCodeRefusal } from '../../../../store/actions/promocodes'
import { settings } from '../../../../store/constants'
import { previewQuery } from '../../../../lib/credit-preview'
import {
  context,
  setIsProcessingStripe,
  setIsStripeError,
  setStripeError,
  useOrdersApi,
  useSelectors,
  useUsersApi
} from '../../../../store'

import { styles } from './styles'

// Once per module: makeStyles called inside a render built a new JSS sheet,
// with new class names, on every render (booking screen, 11 Oct 2026).
const useStyles = makeStyles(styles)

// The three Poppins weights the card form's appearance names, as
// checkout-mode.js elementsOptions wants them.
const POPPINS_FACES = [
  { weight: '400', path: poppinsRegular },
  { weight: '500', path: poppins500 },
  { weight: '600', path: poppins600 }
]

// The API token in use (a guest's after guest_signup).
const storedToken = () => {
  try { return window.localStorage.getItem('token') } catch (e) { return null }
}

// User::GUEST_PLACEHOLDER_DOMAIN: a mobile-only guest's stand-in address.
const placeholderEmail = email => !email || /@guest\.gowashmycar\.com$/i.test(email)

// THE ONE EMBEDDED CHECKOUT (EMBEDDED_CHECKOUT, G-255): one Stripe Elements
// group under the postcode, house number and mobile. The Express Checkout
// Element leads with Apple Pay on every device (Safari natively; Chrome, Edge
// and Firefox through the iPhone QR flow), then the card form in the Payment
// Element. Why not Stripe's Embedded Checkout: lib/checkout-mode.js.
//
// Deferred intent, setup mode, exactly what the hosted Checkout did: nothing
// is created until the payer has chosen (wallet sheet approved, or the card
// form submitted). Then, in this order: the guest is signed up with the
// mobile; the draft order and its SetupIntent are made from the address
// (api/v2/orders/embedded_checkout); Stripe.js confirms in the page; the
// success page finalizes by SetupIntent, as Stripe's return_url does after a
// 3DS or Link redirect and the setup_intent.succeeded webhook does if the tab
// is gone.
//
// WHERE THE CAR IS (18 Oct 2026, amending 5 Oct's "never a shipping
// address"). Card: the postcode, house number and mobile typed in the card
// section (`cardFields`). Wallet: the sheet's address, phone and email; a
// wallet payer types nothing on the page (Robin: "yea I think we need to be
// able to get the details from apple or google pay", "fewest boxes and
// fields is what Im thinking.."). While the sheet is open its redacted
// address is checked by the server (shippingaddresschange: covered, and the
// Congestion Charge rate where the district can be in the zone) and the
// sheet's total is updated before Face ID; at confirm its full address and
// phone go to the same guest sign-up and embedded_checkout
// (lib/wallet-address.js).
//
// On every device the card section waits, collapsed, behind "Or pay by card"
// under the wallet row, and opens when that is tapped (Robin, 7 Oct: "I would
// expand the card section once clicked the pay by card button, otherwise they
// go for apple or google or online.."). With no wallet at all it starts open:
// lib/checkout-mode.js cardSectionOpen.
//
// `unpaid` (from StepBook): what the line above the wallet button says while
// the booking is unpaid (lib/unpaid-status.js): { missing (form field names),
// alert (a refused press, still missing something), errorMark (this line is
// the one data-book-error note), returned (back on an unpaid form),
// onShowMissing (go to the first missing field) }.
//
// `canPay`: the wallet may open its sheet (the taps are done: wash, size,
// day). `canPayCard`: the card may go (the taps and the typed fields).
// `cardFields`: the postcode, house number and mobile, shown in the card
// section only. `pageZone`: the page's congestion tick. `onPathChange(path)`:
// 'wallet' while the wallet is the way to pay, 'card' once the card section
// is open (or there is no wallet), so the page asks only what that path needs.
export const OneCheckout = ({ canPay, canPayCard = canPay, cardFields = null, pageZone = false, onPathChange = null, onBeforePay, termsAccepted, optedInWhatsappMarketing, typedMobile, amountPence, washDate, totalText, unpaid = null }) => {
  const classes = useStyles()
  const { dispatch } = useContext(context)
  const { user, isUserSignedIn, isProcessingStripe, promoCode, promoCodeInfo } = useSelectors()
  // A code on the booking: the press then reads the card first (lib/card-token.js).
  const promoCodeHeld = (promoCode?.code || promoCodeInfo?.code || '').toString()
  const { apiCreateEmbeddedCheckout, apiWalletAddress, handleCheckout } = useOrdersApi()
  // Stripe.js failed to load, retry included (lib/stripe.js): no Elements can
  // mount, so the card goes through Stripe's hosted page instead.
  const stripeJsFailed = useStripeLoadFailed()
  const { apiGuestSignUp, apiUserUpdate } = useUsersApi()

  const expressRef = useRef(null)
  const cardRef = useRef(null)
  const stripeRef = useRef(null)
  const elementsRef = useRef(null)
  // This tab's attempt (lib/payer-email.js): the key the server reuses one
  // draft and SetupIntent by, the guest already signed up, the typed email.
  // Read once, so a reload or a 3DS return keeps the email (12 Oct 2026).
  const attempt = useRef(null)
  if (!attempt.current) attempt.current = saveAttempt({})
  const [typedEmail, setTypedEmail] = useState(attempt.current.email || '')
  const [emailError, setEmailError] = useState('')
  // A failed card confirm, said under the Book button with the form kept.
  const [cardError, setCardError] = useState('')
  // A confirm still with the bank after a while, or after a trip away.
  const [bankWait, setBankWait] = useState(false)

  const [ready, setReady] = useState(false)
  const [loadFailed, setLoadFailed] = useState(false)
  // null until the Express Checkout Element says which wallets it can show.
  const [wallets, setWallets] = useState(null)
  // Stripe.js is in (lib/stripe.js); until then no "Or pay by card".
  const [stripeReady, setStripeReady] = useState(false)
  // The payer tapped "Or pay by card" (and has not tapped it shut again).
  const [cardTapped, setCardTapped] = useState(false)
  // 'apple' | 'android' | 'other', once (lib/checkout-mode.js platformOf).
  const [platform] = useState(() => platformOf())
  // WALLET_ADDRESS_FROM_SHEET (lib/checkout-mode.js): the sheet gives the
  // address and the phone. Off: everything below is as before 18 Oct.
  const [fromSheet] = useState(() => walletAddressFromSheet())
  // The card form's own Apple Pay or Google Pay tab, when that is the one
  // chosen in it (cardFormWallet); null for the card.
  const cardFormTab = useRef(null)
  const focusOnOpen = useRef(false)
  // "Now tap Apple Pay" (lib/wallet-sheet.js walletNudgeText): a wallet tap
  // was refused for a missing field, and that field is now filled in. The
  // wallet the refused tap was for waits in refusedWallet until then; the
  // line goes on the next tap, wallet or card.
  const refusedWallet = useRef(null)
  const [nudge, setNudge] = useState(null)
  const nudgeRef = useRef(null)
  nudgeRef.current = nudge
  const walletAreaRef = useRef(null)

  // A signed-in customer with a real email pays as that address (as the
  // hosted Checkout locked it to the Customer's). Everyone else gives one:
  // the wallet hands over its own, and the card path asks for it here, where
  // Stripe's hosted page used to. Order mail goes to it (W23(a), G-251).
  // Our own field, not Stripe's Link email element: that one is required
  // once mounted, which would block a signed-in customer whose status lands
  // after the mount.
  const knownEmail = isUserSignedIn && !placeholderEmail(user?.email) ? user.email : ''
  const needsEmail = !knownEmail

  // The account credit the server will apply to this booking, asked again
  // whenever the mobile, the email or the account changes
  // (lib/credit-preview.js). The summary shows only that.
  const creditMobile = typedMobile || (isUserSignedIn ? user?.mobile || '' : '')
  const creditEmail = knownEmail || typedEmail
  useEffect(() => {
    const query = previewQuery({ signedIn: !!isUserSignedIn, mobile: creditMobile, email: creditEmail })
    if (!query) {
      dispatch(setCreditPreview(0))
      return undefined
    }
    let live = true
    const timer = setTimeout(() => {
      const token = storedToken()
      fetch(`${settings.host}/api/v2/credits/preview`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'X-User-Token': token } : {}) },
        body: JSON.stringify(query)
      })
        .then(res => res.json())
        .then(json => { if (live) dispatch(setCreditPreview(json?.status === 'ok' ? json.data?.credit_cents || 0 : 0)) })
        .catch(() => { if (live) dispatch(setCreditPreview(0)) })
    }, 300)
    return () => {
      live = false
      clearTimeout(timer)
    }
  }, [dispatch, isUserSignedIn, creditMobile, creditEmail, user?.id, user?.credit_cents])

  // The Stripe handlers are bound once, at mount; they read the booking
  // screen's current answers through this ref.
  const latest = useRef({})
  latest.current = { canPay, canPayCard, pageZone, onBeforePay, termsAccepted, optedInWhatsappMarketing, typedMobile, amountPence, washDate, isUserSignedIn, user, knownEmail, promoCodeHeld }

  // The WhatsApp box for a payer who is already signed in: a tick is saved
  // on the account (the server never lets it override a STOP). An unticked
  // box changes nothing: it says nothing about an earlier yes.
  const optInSignedIn = l => {
    if (!l.optedInWhatsappMarketing || l.user?.opted_in_whatsapp_marketing) return
    AnalyticsService.trackWhatsappMarketingOptin(true, 'signed_in')
    apiUserUpdate({ opted_in_whatsapp_marketing: true })
  }

  // The open sheet's price (lib/wallet-address.js walletAddressOutcome): the
  // booking without the zone, and the rate chosen. Reset on each click.
  const sheetPrice = useRef(null)
  // The sheet's address was refused (shippingaddresschange: the sheet's own
  // reject() takes no words), or its phone is not a UK mobile (confirm):
  // said here, on the page, under the status line.
  const [walletNotice, setWalletNotice] = useState('')

  // The Elements total, which Apple Pay and Google Pay show (setup mode takes
  // an optional amount). Never 0: Stripe's minimum.
  const setElementsAmount = pence => {
    if (!fromSheet || !elementsRef.current || !(pence > 0)) return
    try { elementsRef.current.update({ amount: pence }) } catch (e) { /* an older Stripe.js: the sheet keeps its own */ }
  }

  // details: what Stripe (or our API) said, code and message, no PII
  // (lib/stripe-load.js), so a wallet failure in PostHog carries its reason.
  const fail = (kind, method, reason, message, details) => {
    dispatch(setIsProcessingStripe(false))
    if (message) dispatch(setStripeError(message))
    dispatch(setIsStripeError(true))
    if (kind === 'wallet') AnalyticsService.trackWalletPaymentFailed(reason, method, details || stripeErrorDetails({ message }))
    else AnalyticsService.trackStripeError(reason, message, details)
    return false
  }

  // Both rails end here once the payer has chosen.
  // skipSubmit: the card form's own wallet tab already ran elements.submit()
  // inside the tap (payByCardFormWallet).
  // wallet: the Express Checkout sheet's { address, postcode, zone, mobile }
  // (confirm event, lib/wallet-address.js); null on the card path and for the
  // card form's own wallet tab, which use what was typed.
  const pay = async ({ kind, method, payerEmail, skipSubmit = false, wallet = null }) => {
    const stripe = stripeRef.current
    const elements = elementsRef.current
    const l = latest.current
    if (!stripe || !elements) return fail(kind, method, 'not_loaded')
    dispatch(setIsStripeError(false))
    dispatch(setIsProcessingStripe(true))
    setCardError('')
    setBankWait(false)
    try {
      // Validates the card form (Stripe paints its own errors) or collects
      // the wallet's payment details; nothing is created yet.
      const { error: submitError } = skipSubmit ? {} : await elements.submit()
      if (submitError) {
        dispatch(setIsProcessingStripe(false))
        if (kind === 'card') AnalyticsService.trackValidationBlocked('book', 'card_incomplete')
        else AnalyticsService.trackWalletPaymentFailed('submit_failed', method, stripeErrorDetails(submitError))
        return false
      }

      // 1. Who is paying: the typed mobile on the card path, the sheet's
      // phone on the wallet path. A retry in this tab pays as the guest it
      // already made (one account, one PostHog person), while the mobile and
      // the WhatsApp answer stand.
      const mobile = wallet ? wallet.mobile : l.typedMobile
      const guestNow = { mobile, optedIn: !!l.optedInWhatsappMarketing, token: storedToken() }
      if (!l.isUserSignedIn && !sameGuest(attempt.current, guestNow)) {
        AnalyticsService.trackSigninMethodSelected('guest')
        AnalyticsService.trackWhatsappMarketingOptin(l.optedInWhatsappMarketing, kind)
        let guest = null
        try {
          guest = await apiGuestSignUp({ mobile, optedInWhatsappMarketing: l.optedInWhatsappMarketing })
        } catch (err) {
          if (/mobile/i.test(err?.message || '')) AnalyticsService.trackFieldBlocked('book', 'mobile', 'not_a_mobile')
          return fail(kind, method, 'guest_signup_failed', customerMessage(err, 'Unable to create guest account. Please try again.'))
        }
        if (!guest?.token) return fail(kind, method, 'guest_signup_failed', 'Unable to create guest account. Please try again.')
        attempt.current = saveAttempt({ guest: { ...guestNow, token: guest.token } })
      } else if (l.isUserSignedIn && mobile && !(wallet ? isValidUkMobile(l.user?.mobile || '') : l.user?.mobile)) {
        // A signed-in payer with no mobile on file (with the sheet: no good
        // one): this one. One on file stays as it is.
        apiUserUpdate({ mobile })
      }
      // A signed-in payer (most often a guest back on the same phone) who
      // ticked the WhatsApp box: until 25 Oct 2026 the tick went nowhere.
      if (l.isUserSignedIn) optInSignedIn(l)

      // 2. The draft order and its SetupIntent, from the typed address or
      // the sheet's. The same key with the same booking gets the same draft
      // and SetupIntent back (Api::V2::OrdersController#embedded_checkout).
      // The card about to be saved, as a ConfirmationToken (nothing is
      // charged or saved by it): the server reads its fingerprint and
      // refuses a referral code paid with the referrer's own card before any
      // money moves (Robin, 26 Oct 2026). Only with a code on the booking;
      // null (no token) keeps the confirm as it was.
      const tokenId = (l.promoCodeHeld ? await confirmationTokenFor(stripe, elements, { kind, payerEmail }) : null)
      const res = await apiCreateEmbeddedCheckout(payerEmail, attempt.current.key,
        wallet ? { address: wallet.address, postcode: wallet.postcode, zone: wallet.zone } : null, tokenId)
      // The code was refused for who is booking: drop it, so the total on
      // the page is what the card pays, and say why before any confirm.
      if (promoRefusedAtPay(res)) {
        // Before the code is dropped: the event reads the total it showed.
        AnalyticsService.trackPromoCodeRejected(res.data, 'pay_press')
        dispatch(setPromoCode(null))
        dispatch(apiPromoCodeInfoSuccess(null))
        dispatch(setPromoCodeRefusal(promoRefusal(res, l.promoCodeHeld)))
        return fail(kind, method, 'promo_refused', res.message, apiErrorDetails(res))
      }
      if (res?.status !== 'ok' || !res.data?.client_secret) {
        // A postcode postcodes.io does not know is refused here, before any
        // card is saved: the same field_blocked the form's own checks send.
        if (/postcode/i.test(res?.message || '')) AnalyticsService.trackFieldBlocked('book', 'postcode', 'postcode_not_found')
        return fail(kind, method, kind === 'wallet' ? 'setup_intent_failed' : 'session_create', res?.message, apiErrorDetails(res))
      }

      // 3. Stripe saves the card in the page. Link may still redirect;
      // Stripe then comes back to the same success URL.
      // A card's 3DS check is Stripe's modal over this page, never a
      // redirect. Many UK banks approve in their own app, and on 12 Oct 2026
      // confirmSetup never settled after that trip (iOS Safari, "Just a sec…"
      // for 20 minutes): lib/confirm-watch.js asks Stripe for the SetupIntent
      // too, so a confirm always ends.
      const secret = res.data.client_secret
      const setupArgs = confirmSetupArgs({ elements, clientSecret: secret, origin: window.location.origin, kind, payerEmail, tokenId })
      const watched = await watchConfirm({
        confirm: () => stripe.confirmSetup(setupArgs),
        retrieve: () => stripe.retrieveSetupIntent(secret),
        onThreeDsStarted: () => AnalyticsService.trackThreeDsStarted(kind),
        onWaiting: () => setBankWait(true)
      })
      const { error } = watched
      const confirmFacts = { left_page: watched.leftPage, confirm_seconds: watched.seconds, settled_by: watched.via }
      if (watched.threeDs || /authentication/.test(error?.code || '')) {
        AnalyticsService.trackThreeDsResult(threeDsStatus(watched), error?.code, { kind, ...confirmFacts })
      }
      if (error && kind === 'card') {
        // On the card path, said where it belongs: an email Stripe refused on
        // the email field, anything else under the Book button. The form
        // and the typed email stay as they are.
        dispatch(setIsProcessingStripe(false))
        // stripe_error says where it was shown (`shown`) and on which try
        // (`attempt`); a 3DS failure keeps Stripe's code (error_code
        // setup_intent_authentication_failure) and an email refusal its
        // param, with email_rejected_by_stripe.
        const onEmail = isEmailError(error) && needsEmailRef.current
        // (A signed-in payer's own address has no field here: Stripe's words.)
        const message = onEmail ? EMAIL_INVALID : isEmailError(error) ? error.message : cardErrorMessage(error)
        AnalyticsService.trackStripeError('confirm', error.message, {
          ...stripeErrorDetails(error),
          attempt: attempt.current.cardTries,
          ...confirmFacts,
          shown: onEmail ? 'email_field' : message === BANK_DIDNT_CONFIRM ? 'bank_didnt_confirm' : 'stripe_message',
          ...(isEmailError(error) && { email_rejected_by_stripe: true })
        })
        if (onEmail) {
          AnalyticsService.trackFieldBlocked('book', 'email', 'stripe_rejected')
          showEmailError(EMAIL_INVALID)
        } else {
          setCardError(message)
        }
        return false
      }
      if (error) return fail(kind, method, 'confirm_failed', error.message, { ...stripeErrorDetails(error), ...confirmFacts })

      if (kind === 'wallet') AnalyticsService.trackWalletPaymentCompleted(method)
      dispatch(setIsProcessingStripe(false))
      const successUrl = `/checkout/success/embedded?setup_intent=${encodeURIComponent(res.data.setup_intent_id)}`
      // Found by the check, Stripe's challenge may still be on screen: a full
      // load clears it.
      if (watched.via === 'check') window.location.assign(successUrl)
      else browserHistory.push(successUrl)
      return true
    } catch (err) {
      console.error('[Embedded checkout] error:', err)
      return fail(kind, method, 'exception', customerMessage(err), stripeErrorDetails(err))
    }
  }

  const needsEmailRef = useRef(needsEmail)
  needsEmailRef.current = needsEmail

  const showEmailError = message => {
    setEmailError(message)
    const field = document.getElementById('book-email')
    if (field) field.focus({ preventScroll: false })
  }

  const payRef = useRef(pay)
  payRef.current = pay

  // Mount once: Stripe.js loads on demand (lib/stripe.js), never in the bundle.
  useEffect(() => {
    let cancelled = false
    let elements = null
    getStripe()
      .then(stripe => {
        if (cancelled) return
        if (!stripe) { setLoadFailed(true); return }
        stripeRef.current = stripe
        setStripeReady(true)
        elements = stripe.elements(elementsOptions(window.location.origin, POPPINS_FACES))
        elementsRef.current = elements

        // One wallet button, chosen by platform before the element is made
        // (checkout-mode.js expressOptions). Made once, never remade.
        const sheet = walletSheetWatch({
          opened: AnalyticsService.trackWalletSheetOpened,
          cancelled: AnalyticsService.trackWalletSheetCancelled
        })
        const express = elements.create('expressCheckout', expressOptions(platform, fromSheet))
        express.on('ready', ({ availablePaymentMethods: a }) => {
          if (cancelled) return
          const shown = Object.keys(a || {}).filter(k => a[k])
          setWallets(shown)
          if (shown.length) AnalyticsService.trackWalletAvailable(shown.map(walletMethodOf), walletMethodOf(shown[0]))
          setElementsAmount(latest.current.amountPence)
        })
        express.on('loaderror', () => setWallets([]))
        // The sheet has to open inside the tap, so this answer is synchronous:
        // the booking screen's own verdict (canPay), as the legacy button had.
        // An early tap is not dead: the sheet stays shut and onBeforePay
        // paints the same inline errors the card button does.
        express.on('click', event => {
          const l = latest.current
          setNudge(null)
          if (!l.termsAccepted || !l.canPay) {
            event.reject()
            // Refused for a missing field: once it is filled in, the
            // wallet is brought back into view with "Now tap Apple Pay".
            refusedWallet.current = l.termsAccepted ? event.expressPaymentType : null
            if (l.termsAccepted && l.onBeforePay) l.onBeforePay('wallet')
            return
          }
          refusedWallet.current = null
          setWalletNotice('')
          sheetPrice.current = null
          setElementsAmount(l.amountPence)
          const options = {}
          if (event.expressPaymentType === 'apple_pay') {
            const applePay = applePayRequest({ amountPence: l.amountPence, washDate: l.washDate, origin: window.location.origin })
            if (applePay) options.applePay = applePay
          }
          event.resolve(options)
          // The sheet is opening (wallet_sheet_opened); wallet_payment_started
          // still waits for the payer to approve it (confirm).
          sheet.opened(event.expressPaymentType)
        })
        // The sheet was shut without paying (wallet_sheet_cancelled). The
        // next tap sets the Elements total back to the page's (click).
        express.on('cancel', () => sheet.cancelled())

        // The sheet's address, chosen or changed (redacted: country, town,
        // part of the postcode). The server says whether we wash there and
        // what the Congestion Charge rate adds; the sheet's total is brought
        // up to date before Face ID. Our own outage never blocks: no answer
        // and the page's price stands.
        express.on('shippingaddresschange', async event => {
          const method = platformWallet(platform)
          const l = latest.current
          // The country and the redacted postcode only, nothing else of it.
          const a = event.address || {}
          const check = await apiWalletAddress({ country: a.country, postal_code: a.postal_code })
          const outcome = walletAddressOutcome({ check, pageAmountPence: l.amountPence, pageZone: l.pageZone })
          const outward = outcome.outward
          if (!outcome.ok) {
            sheetPrice.current = null
            setWalletNotice(outcome.message)
            AnalyticsService.trackWalletAddressRejected(outcome.reason, { method, postcode_outward: outward })
            event.reject()
            return
          }
          setWalletNotice('')
          sheetPrice.current = { basePence: outcome.basePence, rate: outcome.shippingRates[0] }
          setElementsAmount(outcome.amountPence)
          AnalyticsService.trackWalletAddressChanged({
            method,
            postcode_outward: outward,
            outside_usual_area: !!check?.outside_usual_area,
            may_be_in_congestion_zone: outcome.mayBeInZone,
            price_changed: outcome.priceChanged
          })
          event.resolve({ shippingRates: outcome.shippingRates, lineItems: outcome.lineItems })
        })

        // The payer picked the other rate (in the zone, or not).
        express.on('shippingratechange', event => {
          const base = sheetPrice.current ? sheetPrice.current.basePence : latest.current.amountPence
          sheetPrice.current = { basePence: base, rate: event.shippingRate }
          setElementsAmount(amountForRate(base, event.shippingRate))
          // The wash line stays as shippingaddresschange set it; line + rate
          // = the new total.
          event.resolve({})
        })

        express.on('confirm', async event => {
          sheet.confirmed()
          const method = walletMethodOf(event.expressPaymentType)
          const l = latest.current
          if (l.onBeforePay && !(await l.onBeforePay('wallet'))) {
            event.paymentFailed({ reason: 'fail' })
            return
          }
          // Switch off: the typed address and mobile, as before (wallet null).
          let wallet = null
          if (fromSheet) {
            // Where the car is: the sheet's full address (only now unredacted).
            const place = walletAddressFromShipping(event.shippingAddress)
            if (!place.complete) {
              AnalyticsService.trackWalletAddressRejected('no_postcode', { method, stage: 'confirm' })
              setWalletNotice(NO_POSTCODE)
              event.paymentFailed({ reason: 'invalid_shipping_address', message: NO_POSTCODE })
              return
            }
            // The mobile: the sheet's phone, by the form's rules; else one on
            // file, else one typed in the card section's box. None of them (a
            // foreign or landline number in the wallet): the card section
            // opens on its mobile box, and the next wallet tap uses it.
            const mobile = walletMobile(event.billingDetails?.phone || '') ||
              (l.isUserSignedIn && walletMobile(l.user?.mobile || '')) || walletMobile(l.typedMobile)
            if (!mobile) {
              AnalyticsService.trackWalletPaymentFailed('phone_not_uk_mobile', method, {})
              setWalletNotice('Add a UK mobile below, then tap again.')
              setCardTapped(true)
              event.paymentFailed({ reason: 'fail' })
              return
            }
            wallet = { address: place.address, postcode: place.postcode, zone: zoneFromRate(event.shippingRate ?? sheetPrice.current?.rate), mobile }
          }
          AnalyticsService.trackPaymentMethodSelected(method)
          AnalyticsService.trackWalletPaymentStarted(method)
          const ok = await payRef.current({ kind: 'wallet', method, payerEmail: event.billingDetails?.email || l.knownEmail, wallet })
          if (!ok) event.paymentFailed({ reason: 'fail' })
        })
        express.mount(expressRef.current)

        // The card form keeps the platform's wallet (Apple Pay on Apple
        // devices, Google Pay on Android; checkout-mode.js
        // paymentElementOptions), so opening it never takes Apple Pay away.
        const card = elements.create('payment', paymentElementOptions(platform))
        card.on('ready', () => setReady(true))
        card.on('change', event => { cardFormTab.current = cardFormWallet(event?.value?.type) })
        card.on('loaderror', () => setLoadFailed(true))
        card.mount(cardRef.current)
      })
      .catch(() => { if (!cancelled) setLoadFailed(true) })
    return () => {
      cancelled = true
      if (!elements) return
      ;['expressCheckout', 'payment'].forEach(type => {
        try { const el = elements.getElement(type); if (el) el.destroy() } catch (e) { /* already gone */ }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // The refused wallet tap's missing fields are all filled in now (canPay):
  // say "Now tap Apple Pay" above the button and bring it back into view.
  // Not while a box is still being typed in: "1" is already a house number,
  // so the page waits until the box is left, and does nothing if the line
  // has gone (a tap) or the booking is incomplete again by then.
  useEffect(() => {
    if (!canPay || !refusedWallet.current) return
    const text = walletNudgeText(refusedWallet.current)
    refusedWallet.current = null
    if (!text) return
    setNudge(text)
    let stopped = false
    let field = null
    const typing = () => {
      const el = typeof document !== 'undefined' ? document.activeElement : null
      return el && /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) ? el : null
    }
    const onLeave = () => { field = null; setTimeout(bringIntoView, 350) }
    const bringIntoView = () => {
      if (stopped || !nudgeRef.current || !latest.current.canPay) return
      field = typing()
      if (field) { field.addEventListener('blur', onLeave, { once: true }); return }
      const el = walletAreaRef.current
      if (!el) return
      try { el.scrollIntoView({ block: 'center', behavior: 'smooth' }) } catch (e) { el.scrollIntoView(false) }
    }
    setTimeout(bringIntoView, 60)
    return () => {
      stopped = true
      if (field) field.removeEventListener('blur', onLeave)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canPay])

  // The payer's email for the card form (lib/payer-email.js), and it NEVER
  // STOPS THE PRESS (Robin, 31 Oct 2026: "we dont validate for fields at pay,
  // if customer is willing to pay we let them and aider resolves missing
  // info.."). This was the fourth and last refusal of ours on the pay path:
  // an address the light check failed got "Check your email address" on the
  // field, `field_blocked`, and no booking at all.
  //
  // It was never Stripe's requirement. The ConfirmationToken's
  // billing_details, confirmSetup's payment_method_data and the SetupIntent's
  // metadata each carry the address only when there is one (lib/card-token.js,
  // User::Stripe#create_embedded_setup_intent), so the Payment Element mounts
  // and confirms without it. The field is ours, and so was the refusal.
  //
  // null means "nothing usable to store", never "stop": the same rule as the
  // mobile (Api::V2::UsersController#guest_signup), because an address we
  // cannot send to must not sit in the record looking like a way to reach
  // somebody. The booking goes, the guest keeps its placeholder address,
  // Order#missing_details reports :email and Aider asks for it.
  //
  // Synchronous, so the card form's wallet tab can still open its sheet
  // inside the tap.
  const cardFormEmail = () => {
    const l = latest.current
    // Tidied without asking (spaces, case, a stray comma or dot) and shown
    // back in the field. This is what fixes real typos, and it stays.
    const { email: payerEmail, problem, rule, fixes } = cardPayerEmail({ typed: typedEmail, known: l.knownEmail })
    if (!l.knownEmail && payerEmail !== typedEmail) {
      setTypedEmail(payerEmail)
      attempt.current = saveAttempt({ email: payerEmail })
    }
    if (fixes.length) AnalyticsService.trackEmailCleaned(fixes)
    if (problem) {
      // Not a refusal, so not `field_blocked` and nothing red on the field:
      // this counts the bookings Aider has to ask an address for
      // (ANALYTICS.md `email_unusable`).
      AnalyticsService.trackEmailUnusable(problem, problem === 'missing' ? null : rule)
      return null
    }
    return payerEmail
  }

  // Apple Pay or Google Pay chosen INSIDE the card form (17 Oct 2026). The
  // sheet has to open inside the tap, so elements.submit() runs before any
  // await, and the booking's check is the synchronous one the wallet button
  // makes (canPayCard: this tab pays with the typed details, as the card
  // does). A tap that cannot go yet paints the same inline errors.
  const payByCardFormWallet = async type => {
    const l = latest.current
    const method = walletMethodOf(type)
    if (!l.termsAccepted || !l.canPayCard) {
      if (l.termsAccepted && l.onBeforePay) l.onBeforePay('card')
      return
    }
    // null: no usable address, which is not a reason to refuse a wallet tap.
    const payerEmail = cardFormEmail()
    const elements = elementsRef.current
    if (!elements) { fail('wallet', method, 'not_loaded'); return }
    if (type === 'apple_pay') {
      const applePay = applePayRequest({ amountPence: l.amountPence, washDate: l.washDate, origin: window.location.origin })
      try { if (applePay) elements.getElement('payment')?.update({ applePay }) } catch (e) { /* the sheet still opens */ }
    }
    const submitted = elements.submit()
    AnalyticsService.trackWalletSheetOpened({ method, source: 'card_form' })
    const { error: submitError } = await submitted
    if (submitError) {
      AnalyticsService.trackWalletPaymentFailed('submit_failed', method, { ...stripeErrorDetails(submitError), source: 'card_form' })
      return
    }
    if (l.onBeforePay && !(await l.onBeforePay('card'))) return
    AnalyticsService.trackPaymentMethodSelected(method)
    AnalyticsService.trackWalletPaymentStarted(method)
    await pay({ kind: 'wallet', method, payerEmail, skipSubmit: true })
  }

  const payByCard = async () => {
    setNudge(null)
    if (cardFormTab.current) return payByCardFormWallet(cardFormTab.current)
    const l = latest.current
    if (l.onBeforePay && !(await l.onBeforePay('card'))) return
    // null: no usable address, and the booking goes without one.
    const payerEmail = cardFormEmail()
    attempt.current = saveAttempt({ cardTries: (attempt.current.cardTries || 0) + 1 })
    AnalyticsService.trackPaymentMethodSelected('card')
    AnalyticsService.trackCardPaymentStarted(attempt.current.cardTries)
    await pay({ kind: 'card', payerEmail })
  }

  // Stripe.js never loaded: the same guest step as pay(), then Stripe's
  // hosted card page by URL (handleCheckout with no Stripe instance).
  const payOnStripePage = async () => {
    const l = latest.current
    if (l.onBeforePay && !(await l.onBeforePay('card'))) return
    AnalyticsService.trackStripeFallbackTapped()
    dispatch(setIsStripeError(false))
    if (!l.isUserSignedIn) {
      AnalyticsService.trackSigninMethodSelected('guest')
      AnalyticsService.trackWhatsappMarketingOptin(l.optedInWhatsappMarketing, 'card')
      let guest = null
      try {
        guest = await apiGuestSignUp({ mobile: l.typedMobile, optedInWhatsappMarketing: l.optedInWhatsappMarketing })
      } catch (err) {
        if (/mobile/i.test(err?.message || '')) AnalyticsService.trackFieldBlocked('book', 'mobile', 'not_a_mobile')
        fail('card', 'card', 'guest_signup_failed', err?.message || 'Unable to create guest account. Please try again.')
        return
      }
      if (!guest?.token) { fail('card', 'card', 'guest_signup_failed', 'Unable to create guest account. Please try again.'); return }
    } else {
      if (!l.user?.mobile && l.typedMobile) apiUserUpdate({ mobile: l.typedMobile })
      optInSignedIn(l)
    }
    handleCheckout(null)
  }

  // Stripe.js gone (retry included): the card section is the way to pay, by
  // Stripe's own page, in the same place and the same button as before.
  // "Or pay by card" waits for Stripe.js to have loaded (28 Oct 2026). Shown
  // while it was still loading, it was taken away, or opened by itself, a
  // second or two later when the load gave up, under the finger about to tap
  // it (CI iphone-webkit, form-v3 "pay asks, never refuses", three times).
  // Now nothing on screen changes its mind: the trigger appears once there
  // are wallets to put it under, or the open section's Pay by card does.
  const cardOpen = stripeJsFailed || cardSectionOpen({ wallets, tapped: cardTapped, loadFailed })
  const cardTrigger = stripeReady && !stripeJsFailed && showCardTrigger({ wallets, loadFailed })

  // "Or pay by card" (lib/checkout-mode.js cardTriggerTap): a second tap
  // right after it opened keeps it open, and the tap that opens an
  // incomplete booking says what is missing, as a refused wallet tap does.
  const cardOpenedAt = useRef(0)
  const cardSectionRef = useRef(null)
  // With a mouse, bring the opened form on screen once it has grown (180 ms):
  // on a 705 px Mac window it opened below the fold (replay 01a14e90). On a
  // phone the keyboard raised for the email field does this already.
  const showCardSection = () => setTimeout(() => {
    const el = cardSectionRef.current
    if (!el || !window.matchMedia?.('(pointer: fine)').matches || el.getBoundingClientRect().bottom <= window.innerHeight) return
    try { el.scrollIntoView({ block: 'nearest', behavior: 'smooth' }) } catch (e) { el.scrollIntoView(false) }
  }, 220)
  const toggleCard = () => {
    setNudge(null)
    refusedWallet.current = null
    const l = latest.current
    // Terms unticked: nothing to say about the fields (as the wallet tap).
    // Only the taps count here (canPay): the typed fields are in the section
    // this tap opens, so they are not "missing" yet.
    const tap = cardTriggerTap({ open: cardTapped, openedAt: cardOpenedAt.current, now: Date.now(), complete: !!l.canPay || !l.termsAccepted })
    if (tap.action === 'close') { setCardTapped(false); return }
    if (tap.action === 'open') {
      cardOpenedAt.current = Date.now()
      focusOnOpen.current = !tap.showMissing
      setCardTapped(true)
      AnalyticsService.trackCardFormOpened()
    }
    // onBeforePay paints the unpaid line red and goes to the first
    // missing field (or asks the car size and day right here).
    if (tap.showMissing && l.termsAccepted && l.onBeforePay) l.onBeforePay('card')
    else showCardSection()
  }

  // Focus the first field once the section is open. A layout effect runs
  // inside the tap's own task, so iOS still raises the keyboard for the email
  // field. The Payment Element focuses through Stripe (a no-op on iOS).
  useLayoutEffect(() => {
    if (!cardOpen || !focusOnOpen.current) return
    focusOnOpen.current = false
    // The card section's first box: the postcode (cardFields), else the email.
    const first = cardSectionRef.current?.querySelector('input')
    if (first) { first.focus({ preventScroll: true }); return }
    try { elementsRef.current?.getElement('payment')?.focus() } catch (e) { /* not mounted yet */ }
  }, [cardOpen])

  const cardDisabled = isProcessingStripe || !termsAccepted || loadFailed

  // THE UNPAID LINE (17 Oct 2026, lib/unpaid-status.js): one line right
  // above the wallet button, on screen for as long as the booking is unpaid.
  // It replaced the warning that vanished the moment AutoFill filled the
  // field (replay 01a1497c). Since 19 Oct it names the one next thing ("Next:
  // your car size", a link there), or "All set. Tap Apple Pay to book." The
  // "Now tap Apple Pay" nudge is this line, saying "now".
  const noWallet = stripeJsFailed || loadFailed || (Array.isArray(wallets) && wallets.length === 0)
  // Which path the page is asking for (18 Oct 2026): the wallet, which needs
  // only taps, until the card section is open or there is no wallet.
  // Switch off: the card's fields are on the page for every path, and the
  // line names the wallet whenever there is one, as before.
  const path = noWallet || (fromSheet && cardOpen) ? 'card' : 'wallet'
  useEffect(() => { if (onPathChange) onPathChange(path) }, [path, onPathChange])
  const shownWallet = path === 'card' ? null : Array.isArray(wallets) ? walletMethodOf(wallets[0]) : platformWallet(platform)
  const missing = unpaid?.missing || []
  const status = unpaidStatus({ missing, wallet: shownWallet, returned: !!unpaid?.returned, nudged: !!nudge && canPay })
  // unpaid_status_shown, once per reason per page view, when the line is
  // at least half on screen (not on load, three screens above it).
  const statusRef = useRef(null)
  const statusSeen = useRef(new Set())
  const statusVisible = useRef(false)
  const statusNow = useRef(null)
  statusNow.current = { reason: status.reason, missing: analyticsMissing(missing), wallet: shownWallet || 'card', missingCount: status.missingCount, nextField: status.nextField }
  const reportStatus = () => {
    const now = statusNow.current
    if (!statusVisible.current || !now || statusSeen.current.has(now.reason)) return
    statusSeen.current.add(now.reason)
    AnalyticsService.trackUnpaidStatusShown(now.reason, now.missing, now.wallet, now.missingCount, now.nextField)
  }
  useEffect(() => {
    const el = statusRef.current
    if (!el || typeof IntersectionObserver === 'undefined') return undefined
    const io = new IntersectionObserver(entries => {
      statusVisible.current = entries.some(e => e.isIntersecting)
      reportStatus()
    }, { threshold: 0.5 })
    io.observe(el)
    return () => io.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripeJsFailed])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(reportStatus, [status.reason])

  // One line on a phone (19 Oct 2026): "3 quick details, then Apple Pay.
  // Next: your car size" drops "then Apple Pay" when it would wrap. Measured,
  // not guessed: the full line is drawn first and swapped for the short one
  // before paint if it takes two lines; a resize measures again.
  const statusTextRef = useRef(null)
  const [shortFor, setShortFor] = useState(null)
  const shortLine = shortFor === status.text && status.leadShort !== status.lead
  useLayoutEffect(() => {
    const el = statusTextRef.current
    if (!el || shortLine || status.leadShort === status.lead) return
    const lineHeight = parseFloat(window.getComputedStyle(el).lineHeight) || 20
    if (el.offsetHeight > lineHeight * 1.5) setShortFor(status.text)
  })
  useEffect(() => {
    const onResize = () => setShortFor(null)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])
  const statusLead = shortLine ? status.leadShort : status.lead
  const statusLine = (
    <p
      ref={statusRef}
      className={clsx(classes.unpaidStatus, unpaid?.alert && classes.unpaidStatusAlert, !missing.length && nudge && canPay && classes.unpaidStatusNudge)}
      role={unpaid?.alert ? 'alert' : 'status'}
      data-testid="unpaid-status"
      data-reason={status.reason}
      data-next-field={status.nextField || undefined}
      {...(unpaid?.errorMark ? { 'data-book-error': '' } : {})}>
      {unpaid?.alert && <ErrorOutlineRounded fontSize="small" />}
      <span ref={statusTextRef}>
        {statusLead}
        {status.link && (
          <>
            {' '}
            {unpaid?.onShowMissing ? (
              <button type="button" className={classes.unpaidStatusLink} onClick={unpaid.onShowMissing} data-testid="unpaid-status-next">{status.link}</button>
            ) : status.link}
          </>
        )}
      </span>
    </p>
  )

  // The sheet's address refused, or its phone not a UK mobile: said here.
  const walletNoticeLine = walletNotice ? (
    <p className={classes.cardError} role="alert" data-testid="wallet-notice">
      <ErrorOutlineRounded fontSize="small" />
      <span>{walletNotice}</span>
    </p>
  ) : null

  // ONE TREE WHETHER OR NOT STRIPE.JS LOADED (26 Oct 2026). It used to be a
  // second layout swapped in when the retry gave up, a second or two after
  // the page: the pay button and the mobile box above it were thrown away
  // and drawn again elsewhere, under a finger about to press (iphone-webkit,
  // form-v3 "pay asks, never refuses"). Now the note takes the wallet's
  // place and the same button pays on Stripe's page.
  const fallbackDisabled = isProcessingStripe || !termsAccepted

  return (
    <div className={classes.oneCheckout} data-testid="embedded-checkout">
      {/* Card security either side of the two pay buttons, once each, outside
          the card section so wallet payers see it too. Robin, 12 Oct 2026:
          "we still need to inc something about card security but only either
          side of the booking links.." */}
      <p className={clsx(classes.ctaHelper, classes.securePayment)} data-testid="secure-payment">
        <LockRounded style={{ fontSize: 14, marginRight: 4 }} />
        {securePaymentLine()}
      </p>
      {/* One wallet: Apple Pay on Apple devices, Google Pay on Android, Link
          elsewhere (expressOptions); empty when that one is not there. */}
      <div ref={walletAreaRef}>
        {statusLine}
        {walletNoticeLine}
        {/* Full strength, always (17 Oct 2026): never faded or made
            untappable. A tap that cannot go yet is refused in the click
            handler, which says why on the page. */}
        {stripeJsFailed ? (
          <div className={classes.stripeFallback} role="status" data-testid="stripe-fallback">
            <ErrorOutlineRounded fontSize="small" />
            <span>
              Apple Pay and the card form couldn&rsquo;t load on this connection. You can still pay by card on
              Stripe&rsquo;s secure page.
            </span>
          </div>
        ) : (
          <div
            ref={expressRef}
            className={clsx(classes.oneExpress, Array.isArray(wallets) && wallets.length === 0 && classes.oneExpressNone)}
            data-testid="embedded-express"
          />
        )}
      </div>

      {/* The card, second: "Or pay by card". Under Apple Pay or Google Pay
          a quiet text button, so the wallet is the one that reads as the way
          to pay (17 Oct 2026); elsewhere, under Link, the outlined button. */}
      {cardTrigger && (
        <button
          type="button"
          className={clsx(classes.cardTriggerBtn, platform !== 'other' && classes.cardTriggerQuiet, cardOpen && classes.cardTriggerOpen)}
          onClick={toggleCard}
          aria-expanded={cardOpen}
          aria-controls="embedded-card-section"
          data-testid="embedded-card-trigger">
          {platform === 'other' && <LockRounded style={{ fontSize: 18, marginRight: 8 }} />}
          Or pay by card
          <ExpandMoreRounded className={classes.cardTriggerChevron} />
        </button>
      )}

      {/* Collapsed, not unmounted: Stripe's card iframe stays where it was
          put. It grows downwards only, so nothing above it moves. */}
      <div
        id="embedded-card-section"
        ref={cardSectionRef}
        className={clsx(classes.oneCollapse, cardOpen && classes.oneCollapseOpen)}
        aria-hidden={!cardOpen}
        data-open={cardOpen ? 'true' : 'false'}>
        <div className={classes.oneCollapseInner}>
          <div className={clsx(classes.oneCard, cardTrigger && classes.oneCardBelowTrigger)} data-testid="embedded-card">
            {/* The card path's own fields (18 Oct 2026): the postcode, house
                number and mobile. A wallet payer never sees them; the sheet
                gives all three. */}
            {cardFields}
            {/* The email helper waits for payment: the old "confirmation goes
                here" line, read under a focused email box, looked like a
                booking already made (replay 01a1497c, 17 Oct 2026). */}
            {needsEmail && !stripeJsFailed && (
              <div className={classes.field} data-testid="embedded-email">
                <label className={classes.fieldLabel} htmlFor="book-email">Email</label>
                <TextField
                  id="book-email"
                  fullWidth
                  placeholder="you@example.com"
                  value={typedEmail}
                  onChange={ev => {
                    setTypedEmail(ev.target.value)
                    attempt.current = saveAttempt({ email: ev.target.value })
                    if (emailError) setEmailError('')
                    if (cardError) setCardError('')
                  }}
                  error={!!emailError}
                  helperText={emailError || "We'll email your confirmation once you've paid."}
                  InputProps={{ disableUnderline: true, autoComplete: 'email', type: 'email', inputMode: 'email' }}
                />
              </div>
            )}
            <div ref={cardRef} data-testid="embedded-payment" />
            {loadFailed && (
              <p className={classes.ctaHelper}>The card form did not load. Refresh the page and try again.</p>
            )}
            <button
              type="button"
              onClick={stripeJsFailed ? payOnStripePage : payByCard}
              className={clsx(classes.cardCta, (stripeJsFailed ? fallbackDisabled : cardDisabled) && classes.cardCtaDisabled)}
              disabled={stripeJsFailed ? fallbackDisabled : cardDisabled}
              data-testid={stripeJsFailed ? 'stripe-fallback-pay' : undefined}
              data-ready={ready ? 'true' : 'false'}>
              <LockRounded style={{ fontSize: 18, marginRight: 8 }} />
              {isProcessingStripe ? 'Just a sec…' : stripeJsFailed ? <>Pay by card, {totalText}</> : <>Book my wash, {totalText}</>}
              {isProcessingStripe && <CircularProgress size={18} className={classes.btnSpinner} />}
            </button>
            {bankWait && isProcessingStripe && (
              <p className={classes.ctaHelper} role="status" data-testid="embedded-bank-wait">{WAITING_FOR_BANK}</p>
            )}
            {cardError && (
              <p className={classes.cardError} role="alert" data-testid="embedded-card-error">
                <ErrorOutlineRounded fontSize="small" />
                <span>{cardError}</span>
              </p>
            )}
          </div>
        </div>
      </div>
      <p className={classes.ctaHelper} data-testid="charge-line">{bookPayChargeLine()}</p>
    </div>
  )
}
